import React, { forwardRef } from "react";
import { useNavigate } from "react-router-dom";

//MUI
import styled from "@emotion/styled";
import Typography from "@mui/material/Typography";

const ServiceContainer = styled("div")(({ theme, scale }) => ({
  width: "250px",
  height: "auto",
  minHeight: "260px",
  padding: "1rem",
  background: "#FFFFFF",
  border: "1px solid #ECF0FC",
  borderRadius: "15px",
  cursor: "pointer",
  transition: "transform 0.3s ease, box-shadow 0.3s ease, border 0.3s ease",
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  "& .img_container": {
    width: "100%",
    height: "120px",
    "& .service_img": {
      width: "100%",
      height: "120px",
      borderRadius: "8px",
    },
  },
  "& .event_desc": {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    gap: "0.1rem",

    "& .heading_text": {
      color: "#1B1C1E",
      fontWeight: "600",
      fontSize: "12px",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    "& .description_text": {
      color: "#4F4F4F",
      fontSize: "12px",
      fontWeight: "400",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    "& .desc_with_readmore": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    "& .read_more": {
      color: "#007bff",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    "& .tags_container": {
      display: "flex",
      gap: "0.5rem",
      marginTop: "5px",
      "& .tags_heading": {
        color: "#1B1C1E",
        fontSize: "12px",
        fontWeight: "400",
      },
      "& .tags_block": {
        display: "flex",
        flexWrap: "wrap",
        gap: "0.5rem",
        flexWrap: "nowrap",
        overflow: "hidden",

        "& .tag_item": {
          background: "#ECF0FC",
          color: "#253A6A",
          fontSize: "10px",
          fontWeight: "400",
          padding: "2px 6px",
          borderRadius: "4px",
          marginRight: "4px",
          whiteSpace: "nowrap",
          display: "inline-block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
          boxSizing: "border-box",
        },
      },
    }
  },
  "& .sources_container": {
    marginTop: "0.3rem",

    "& .tags_block": {
      display: "flex",
      alignItems: "center",
      gap: "0px",

      "& .source_icon_link": {
        background: "none !important",
        padding: 0,
        border: "none",
        display: "inline-flex",
        alignItems: "center",
        "&:not(:first-of-type)": {
        marginLeft: "-6px",
      },
      },

      "& .source_icon": {
        borderRadius: "50%",
        width: "20px",
        height: "20px",
        display: "block",
      },
    },
  },

  "&:hover": {
    boxShadow: "0px 2px 9px 0px #23B1EC40",
    border: "1px solid #00A0E3",
    transform: "translateY(-1px)",
  },
}));

const EventsCardAI = forwardRef(({ data, handleEventSelect, scale = 1.1 }, ref) => {
  const navigate = useNavigate();

  return (
    <ServiceContainer
      ref={ref}
      scale={scale}
      onClick={() => navigate(`/app/explore_ai/events/${data?.event_id}`)}>
        
      {/* Image */}
      <div className="img_container">
        <img
          src={data?.images?.[0] || ""}
          alt={data?.title}
          className="service_img"
        />
      </div>

      <div className="event_desc">

        {/* Title */}
        <Typography className="heading_text">{data?.title}</Typography>

        {/* Read More Button */}
        <div className="desc_with_readmore">
          <Typography className="description_text">
            {data?.title?.length > 10 && (
              <span
                className="read_more"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/app/explore_ai/events/${data?.event_id}`);
                }}
              >
                Read More
              </span>
            )}
          </Typography>
        </div>

        {/* Tags */}
        <div className="tags_container">
          <Typography className="tags_heading">Keywords:</Typography>
          <div className="tags_block">
            {[
              data?.loc_ind?.length > 0 &&
              data.loc_ind.reduce((a, b) => (a.importance > b.importance ? a : b)).name,
              data?.org_political_ind?.length > 0 &&
              data.org_political_ind.reduce((a, b) => (a.importance > b.importance ? a : b)).name,
              data?.person_political_ind?.length > 0 &&
              data.person_political_ind.reduce((a, b) => (a.importance > b.importance ? a : b)).name,
            ]
              .filter(Boolean)
              .map((tag, idx, arr) => {
                const isMultiWord = tag.split(" ").length > 1;
                const totalLength = arr.reduce((acc, t) => acc + t.length, 0);
                const shrink = isMultiWord && totalLength > 30;
                return (
                  <div
                    key={idx}
                    className="tag_item"
                    title={tag}
                    style={{
                      fontSize: shrink ? "10px" : "12px",
                      maxWidth: shrink ? "60px" : "auto",
                    }}
                  >
                    {tag}
                  </div>
                );
              })}
            {(!data?.loc_ind?.length && !data?.org_political_ind?.length && !data?.person_political_ind?.length) && (
              <div className="tag_item">No tags</div>
            )}
          </div>
        </div>

        {/* Sources */}
        {data?.sources && data.sources.length > 0 && (
          <div className="tags_container sources_container">
            <Typography className="tags_heading">Sources:</Typography>
            <div className="tags_block">
              {data.sources.map((src, idx) => {
                const url = new URL(src);
                const favicon = `https://www.google.com/s2/favicons?sz=32&domain_url=${url.hostname}`;
                return (
                  <a
                    key={idx}
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="source_icon_link"
                  >
                    <img
                      src={favicon}
                      alt={url.hostname}
                      className="source_icon"
                      style={{ width: 20, height: 20 }}
                    />
                  </a>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </ServiceContainer>
  );
});
export default EventsCardAI;